import { useState } from 'react';
import { cn } from '../../lib/cn';
import Button from './Button';

interface PhoneRevealProps {
  phone: string;
  /** 'button' — a pill CTA trigger (Contact page) that swaps to plain text
   * once revealed, so the number reads like normal copy rather than staying
   * a button. 'link' — an inline text link matching surrounding copy
   * (Footer), both before and after reveal. */
  variant?: 'button' | 'link';
  /** Applied to the revealed tel: link in the 'link' variant, so it matches
   * the surrounding design once clicked. Ignored for 'button', which has its
   * own fixed plain-text style once revealed. */
  linkClassName?: string;
}

/** Keeps the phone number out of the initial DOM entirely — scrapers and
 * crawlers that don't execute a real click never see the digits, only a
 * "View Number" trigger. Clicking swaps in the real tel: link. */
export default function PhoneReveal({ phone, variant = 'button', linkClassName }: PhoneRevealProps) {
  const [revealed, setRevealed] = useState(false);
  const telHref = `tel:${phone.replace(/[^+\d]/g, '')}`;

  if (variant === 'button') {
    return revealed ? (
      <a href={telHref} className="text-[17px] font-semibold text-ink no-underline hover:text-olive transition-colors">
        {phone}
      </a>
    ) : (
      <Button type="button" onClick={() => setRevealed(true)}>
        View Number
      </Button>
    );
  }

  if (revealed) {
    return (
      <a href={telHref} className={linkClassName}>
        {phone}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setRevealed(true)}
      className={cn('bg-transparent border-none p-0 cursor-pointer', linkClassName)}
    >
      View Number
    </button>
  );
}
